import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Download, UserPlus, Eye } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import {
  adminListCustomers,
  adminCreateCustomer,
  adminCreateInvoice,
} from "@/lib/admin-invoices.functions";
import { downloadOrderPdf, generateOrderPdf } from "@/lib/order-pdf";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { formatEuro } from "@/lib/catalog";

export const Route = createFileRoute("/_authenticated/admin/facturen")({
  head: () => ({
    meta: [
      { title: "Admin — Facturen" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminInvoicesPage,
});

type LineItem = { name: string; qty: number; unit_price_euros: number };

function AdminInvoicesPage() {
  const { isAdmin, loading } = useAuth();
  const qc = useQueryClient();

  const listFn = useServerFn(adminListCustomers);
  const createCustFn = useServerFn(adminCreateCustomer);
  const createInvFn = useServerFn(adminCreateInvoice);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-customers"],
    queryFn: () => listFn({}),
    enabled: isAdmin,
  });

  const [showNewCust, setShowNewCust] = useState(false);
  const [newCust, setNewCust] = useState({
    email: "",
    full_name: "",
    phone: "",
    customer_type: "consumer" as "consumer" | "business",
    password: "",
  });

  const [selectedUser, setSelectedUser] = useState<string>("");
  const [filter, setFilter] = useState("");
  const [paymentTerm, setPaymentTerm] = useState(14);
  const [status, setStatus] = useState<"pending" | "processing" | "completed" | "cancelled">("pending");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<LineItem[]>([
    { name: "", qty: 1, unit_price_euros: 0 },
  ]);

  const [previewOpen, setPreviewOpen] = useState(false);

  const customers = data?.customers ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c: any) =>
        (c.full_name ?? "").toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q),
    );
  }, [customers, filter]);

  const total = items.reduce((s, it) => s + (it.qty * it.unit_price_euros) * 100, 0);

  const createCustMut = useMutation({
    mutationFn: (input: typeof newCust) => createCustFn({ data: input }),
    onSuccess: (res) => {
      toast.success("Klant aangemaakt");
      setShowNewCust(false);
      setNewCust({ email: "", full_name: "", phone: "", customer_type: "consumer", password: "" });
      qc.invalidateQueries({ queryKey: ["admin-customers"] });
      setSelectedUser(res.user_id);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createInvMut = useMutation({
    mutationFn: async () => {
      if (!selectedUser) throw new Error("Selecteer een klant");
      const cleaned = items
        .filter((it) => it.name.trim() && it.qty > 0)
        .map((it) => ({
          slug: "",
          name: it.name.trim(),
          qty: Math.floor(it.qty),
          unit_price_cents: Math.round(it.unit_price_euros * 100),
        }));
      if (cleaned.length === 0) throw new Error("Minstens één regel met naam");
      return createInvFn({
        data: {
          user_id: selectedUser,
          payment_term_days: paymentTerm,
          notes,
          status,
          line_items: cleaned,
        },
      });
    },
    onSuccess: (res) => {
      const mail = (res as any).email;
      if (mail?.ok) {
        toast.success(`Factuur aangemaakt · e-mail verzonden naar ${res.customer.email}`);
      } else if (res.customer?.email) {
        toast.warning(`Factuur aangemaakt, maar e-mail mislukte (${mail?.reason ?? "onbekend"})`);
      } else {
        toast.success("Factuur aangemaakt (geen e-mail: klant heeft geen e-mailadres)");
      }

      const order = res.order as any;
      downloadOrderPdf(
        {
          order_number: order.order_number,
          status: order.status,
          total_cents: order.total_cents,
          created_at: order.created_at,
          line_items: order.line_items,
          notes: order.notes,
          shipping_address: null,
          customer: res.customer,
        },
        order.payment_term_days,
      );
      setItems([{ name: "", qty: 1, unit_price_euros: 0 }]);
      setNotes("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return <p>Laden…</p>;
  if (!isAdmin) return <p className="text-destructive">Geen toegang.</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Facturen aanmaken</h1>
        <p className="text-sm text-muted-foreground">
          Voeg handmatig een klant toe en maak een factuur. De factuur wordt gekoppeld aan het klantaccount en de PDF wordt direct gedownload.
        </p>
      </div>

      {/* Klant kiezen */}
      <section className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">1. Klant</h2>
          <Button variant="outline" size="sm" onClick={() => setShowNewCust((v) => !v)}>
            <UserPlus className="mr-1 h-4 w-4" />
            {showNewCust ? "Annuleren" : "Nieuwe klant"}
          </Button>
        </div>

        {showNewCust && (
          <div className="grid gap-3 rounded-md border bg-muted/30 p-3 md:grid-cols-2">
            <div>
              <Label>Naam</Label>
              <Input
                value={newCust.full_name}
                onChange={(e) => setNewCust({ ...newCust, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input
                type="email"
                value={newCust.email}
                onChange={(e) => setNewCust({ ...newCust, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Telefoon</Label>
              <Input
                value={newCust.phone}
                onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Type</Label>
              <select
                className="w-full rounded-md border bg-background p-2 text-sm"
                value={newCust.customer_type}
                onChange={(e) =>
                  setNewCust({ ...newCust, customer_type: e.target.value as "consumer" | "business" })
                }
              >
                <option value="consumer">Particulier</option>
                <option value="business">Zakelijk</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Wachtwoord (optioneel — leeg = willekeurig)</Label>
              <Input
                type="text"
                value={newCust.password}
                onChange={(e) => setNewCust({ ...newCust, password: e.target.value })}
                placeholder="min. 8 tekens"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                onClick={() => createCustMut.mutate(newCust)}
                disabled={createCustMut.isPending || !newCust.email || !newCust.full_name}
              >
                {createCustMut.isPending ? "Bezig..." : "Klant opslaan"}
              </Button>
            </div>
          </div>
        )}

        <div className="grid gap-2 md:grid-cols-[1fr,2fr]">
          <Input
            placeholder="Zoeken op naam of e-mail..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select
            className="w-full rounded-md border bg-background p-2 text-sm"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="">— Selecteer klant —</option>
            {isLoading && <option disabled>Laden...</option>}
            {filtered.map((c: any) => (
              <option key={c.id} value={c.id}>
                {(c.full_name ?? "Naamloos")} {c.email ? `· ${c.email}` : ""} ({c.customer_type})
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Factuur regels */}
      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="text-lg font-semibold">2. Factuurregels</h2>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={idx} className="grid gap-2 md:grid-cols-[1fr,90px,120px,120px,40px] md:items-end">
              <div>
                <Label className="text-xs">Omschrijving</Label>
                <Input
                  value={it.name}
                  onChange={(e) => {
                    const c = [...items];
                    c[idx] = { ...c[idx], name: e.target.value };
                    setItems(c);
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Aantal</Label>
                <Input
                  type="number"
                  min={1}
                  value={it.qty}
                  onChange={(e) => {
                    const c = [...items];
                    c[idx] = { ...c[idx], qty: Number(e.target.value) || 0 };
                    setItems(c);
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Stukprijs (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={it.unit_price_euros}
                  onChange={(e) => {
                    const c = [...items];
                    c[idx] = { ...c[idx], unit_price_euros: Number(e.target.value) || 0 };
                    setItems(c);
                  }}
                />
              </div>
              <div className="text-sm">
                <Label className="text-xs">Totaal</Label>
                <div className="rounded-md border bg-muted/30 p-2 text-right">
                  {formatEuro(Math.round(it.qty * it.unit_price_euros * 100))}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Regel verwijderen"
                onClick={() => setItems(items.filter((_, i) => i !== idx))}
                disabled={items.length === 1}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setItems([...items, { name: "", qty: 1, unit_price_euros: 0 }])}
          >
            <Plus className="mr-1 h-4 w-4" /> Regel toevoegen
          </Button>
        </div>

        <div className="grid gap-3 border-t pt-3 md:grid-cols-3">
          <div>
            <Label>Betalingstermijn (dagen)</Label>
            <Input
              type="number"
              min={0}
              value={paymentTerm}
              onChange={(e) => setPaymentTerm(Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label>Status</Label>
            <select
              className="w-full rounded-md border bg-background p-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
            >
              <option value="pending">In afwachting</option>
              <option value="processing">In behandeling</option>
              <option value="completed">Afgerond</option>
              <option value="cancelled">Geannuleerd</option>
            </select>
          </div>
          <div className="flex items-end justify-end text-right">
            <div>
              <div className="text-xs text-muted-foreground">Totaal</div>
              <div className="text-xl font-bold">{formatEuro(total)}</div>
            </div>
          </div>
        </div>
        <div>
          <Label>Opmerking (optioneel)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            disabled={!selectedUser || items.every((it) => !it.name.trim())}
          >
            <Eye className="mr-1 h-4 w-4" />
            Voorbeeld PDF
          </Button>
          <Button
            onClick={() => createInvMut.mutate()}
            disabled={createInvMut.isPending || !selectedUser}
          >
            <Download className="mr-1 h-4 w-4" />
            {createInvMut.isPending ? "Bezig..." : "Factuur aanmaken & PDF downloaden"}
          </Button>
        </div>
      </section>

      {(() => {
        const cust = customers.find((c: any) => c.user_id === selectedUser);
        const cleaned = items
          .filter((it) => it.name.trim() && it.qty > 0)
          .map((it) => ({
            name: it.name.trim(),
            qty: Math.floor(it.qty),
            line_total_cents: Math.round(it.qty * it.unit_price_euros * 100),
          }));
        const draftNumber = "CONCEPT";
        return (
          <PdfPreviewDialog
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            title={`Factuur ${draftNumber} — voorvertoning`}
            description="Concept — nummer en datum worden bij aanmaken definitief"
            fileName={`Factuur-${draftNumber}.pdf`}
            source={{
              kind: "blob",
              getBlobUrl: async () => {
                const doc = await generateOrderPdf(
                  {
                    order_number: draftNumber,
                    status,
                    total_cents: cleaned.reduce((s, it) => s + it.line_total_cents, 0),
                    created_at: new Date().toISOString(),
                    line_items: cleaned,
                    notes,
                    shipping_address: null,
                    customer: cust
                      ? { full_name: cust.full_name ?? null, email: cust.email ?? null }
                      : null,
                  },
                  paymentTerm,
                );
                return doc.output("bloburl") as unknown as string;
              },
            }}
          />
        );
      })()}
    </div>
  );
}
